import React from "react"
import { formatQualityCoverage, QualityBadge } from "./DashboardComponents"
import { DefinitionButton } from "./DefinitionExperience"
import { formatMoney, percentageLabel } from "./formatters"

const number = value => value === null || value === undefined
  ? "Unavailable"
  : Number(value).toLocaleString("en-GB", { maximumFractionDigits: 2 })

const value = (raw, unit) => {
  if (unit === "RON") return formatMoney(raw)
  if (raw === null || raw === undefined) return "Unavailable"
  if (unit === "percentage" || unit === "percent") return `${number(raw)}%`
  return number(raw)
}

const difference = metric => {
  if (metric.delta?.percentage !== null && metric.delta?.percentage !== undefined) {
    return percentageLabel(metric.delta.percentage)
  }
  const absolute = metric.delta?.absolute
  if (!Number.isFinite(absolute)) return "n/a"
  return `${absolute >= 0 ? "+" : "−"}${value(Math.abs(absolute), metric.unit)}`
}

// A side the API does not count, because its rate is Unavailable, shows "—" next to the reason.
const populationValue = (item, side) => (
  item.unavailableReason?.[side] ? "—" : value(item[side], item.unit)
)

const DiagnosticsView = ({ snapshot }) => {
  const diagnostics = snapshot.diagnostics
  if (!diagnostics) return <div className="analytics-state">Diagnostics are unavailable for this selection.</div>
  return (
    <section className="analytics-diagnostics-view" aria-labelledby="diagnostics-title">
      <div className="analytics-view-heading">
        <div>
          <p className="analytics-eyebrow">Operational confidence</p>
          <h2 id="diagnostics-title">Diagnostics & data quality</h2>
          <p>Secondary signals, source coverage and exceptions that qualify the management dashboard.</p>
        </div>
      </div>

      <section className="analytics-diagnostic-indicators" aria-label="Diagnostic indicators">
        {diagnostics.indicators.map(metric => (
          <article className="analytics-kpi-card" key={metric.key}>
            <div className="analytics-kpi-topline">
              <span>Secondary indicator</span>
              <QualityBadge quality={metric.current.quality} />
            </div>
            <h3>{metric.label}</h3>
            <strong className="analytics-kpi-value">{value(metric.current.value, metric.unit)}</strong>
            <div className="analytics-kpi-delta">
              <b>{difference(metric)}</b>
              <span>vs {value(metric.comparison.value, metric.unit)}</span>
            </div>
            <p>{metric.current.quality.reason}</p>
            <DefinitionButton metric={metric} />
          </article>
        ))}
      </section>

      <section className="analytics-diagnostics-panel" aria-labelledby="source-coverage-title">
        <div className="analytics-section-heading">
          <div><p className="analytics-eyebrow">Coverage</p><h2 id="source-coverage-title">Source coverage</h2></div>
        </div>
        <div className="analytics-table-wrap">
          <table className="analytics-data-table analytics-source-coverage" aria-label="Source coverage">
            <thead><tr><th>Source</th><th>Current</th><th>Comparison</th><th>Reason</th></tr></thead>
            <tbody>
              {diagnostics.sourceCoverage.map(source => (
                <tr key={source.key}>
                  <th>{source.label}</th>
                  <td><QualityBadge quality={source.current} /><small>{formatQualityCoverage(source.current)}</small></td>
                  <td><QualityBadge quality={source.comparison} /><small>{formatQualityCoverage(source.comparison)}</small></td>
                  <td>
                    <span>Current: {source.current.reason}</span>
                    {source.comparison.reason !== source.current.reason
                      && <small>Comparison: {source.comparison.reason}</small>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="analytics-diagnostics-panel" aria-labelledby="reconciliation-title">
        <div className="analytics-section-heading">
          <div><p className="analytics-eyebrow">Integrity</p><h2 id="reconciliation-title">Reconciliation checks</h2></div>
        </div>
        <div className="analytics-table-wrap">
          <table className="analytics-data-table" aria-label="Reconciliation checks">
            <thead><tr><th>Check</th><th>Current expected</th><th>Current actual</th><th>Comparison expected</th><th>Comparison actual</th><th>Status</th></tr></thead>
            <tbody>
              {diagnostics.reconciliations.length ? diagnostics.reconciliations.map(check => {
                const reconciled = check.current?.discrepancy === 0 && check.comparison?.discrepancy === 0
                return (
                  <tr key={check.key}>
                    <th>{check.label}</th>
                    <td>{value(check.current?.expected, check.unit)}</td>
                    <td>{value(check.current?.actual, check.unit)}</td>
                    <td>{value(check.comparison?.expected, check.unit)}</td>
                    <td>{value(check.comparison?.actual, check.unit)}</td>
                    <td className={reconciled ? "is-positive" : "is-negative"}>{reconciled ? "Reconciled" : "Discrepancy"}</td>
                  </tr>
                )
              }) : <tr><td colSpan="6">No reconciliation-capable source is available for this snapshot.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <section className="analytics-diagnostics-panel" aria-labelledby="exception-populations-title">
        <div className="analytics-section-heading">
          <div>
            <p className="analytics-eyebrow">Visible exceptions</p>
            <h2 id="exception-populations-title">Populations to monitor</h2>
            <p>These are real populations, never silently absorbed into another metric.</p>
          </div>
        </div>
        <div className="analytics-diagnostic-populations" aria-label="Visible exception populations">
          {diagnostics.populations.map(item => (
            <article key={item.key}>
              <span>{item.label}</span>
              <strong>{populationValue(item, "current")}</strong>
              <small>Comparison {populationValue(item, "comparison")}</small>
              {item.evidenceCoverage && (
                <small>
                  Evidence coverage {value(item.evidenceCoverage.current, "percentage")} · Comparison {value(item.evidenceCoverage.comparison, "percentage")}
                </small>
              )}
              <p>{item.reason}</p>
              {item.unavailableReason?.current && <p>Current: {item.unavailableReason.current}</p>}
              {item.unavailableReason?.comparison && <p>Comparison: {item.unavailableReason.comparison}</p>}
            </article>
          ))}
        </div>
      </section>

      <section className="analytics-diagnostics-panel" aria-labelledby="quality-semantics-title">
        <div className="analytics-section-heading">
          <div><p className="analytics-eyebrow">Reading guide</p><h2 id="quality-semantics-title">Quality semantics</h2></div>
        </div>
        <div className="analytics-quality-semantics" aria-label="Quality state definitions">
          {diagnostics.qualityStates.map(item => (
            <article key={item.key}>
              <QualityBadge quality={{
                state: item.key,
                reason: item.description,
                coverage: { basis: "quality-state-definition" },
              }} />
              <p>{item.description}</p>
            </article>
          ))}
        </div>
      </section>
    </section>
  )
}

export default DiagnosticsView
